import type { ChatMessage } from '@/stores/chat-store'
import type { MessageJobContext, MessageMatchData, RevisionReadyEvent } from '@/types'

// Helper function to format timestamps with date and time
export function formatMessageTime(dateString: string | Date): string {
  const date = new Date(dateString)
  const today = new Date()
  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)

  const timeStr = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })

  if (date.toDateString() === today.toDateString()) {
    return timeStr
  }
  if (date.toDateString() === yesterday.toDateString()) {
    return `Yesterday, ${timeStr}`
  }
  const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  return `${dateStr}, ${timeStr}`
}

// Helper function to get date label (Today, Yesterday, or date)
export function getDateLabel(dateString: string | Date): string {
  const date = new Date(dateString)
  const today = new Date()
  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)

  if (date.toDateString() === today.toDateString()) {
    return 'Today'
  }
  if (date.toDateString() === yesterday.toDateString()) {
    return 'Yesterday'
  }
  return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
}

// Group messages by date
export function groupMessagesByDate(messages: ChatMessage[]) {
  const groups: { [key: string]: ChatMessage[] } = {}

  for (const msg of messages) {
    const dateLabel = getDateLabel(msg.created_at || new Date())
    if (!groups[dateLabel]) {
      groups[dateLabel] = []
    }
    groups[dateLabel].push(msg)
  }

  return groups
}

// Filter out hidden data from user messages for display
export const filterHiddenData = (content: string) =>
  content
    .replace(/\s*<!--MATCH_CONTEXT:.*?-->/gs, '')
    .replace(/\s*<!--MATCH_DATA:[\s\S]*?(-->|$)/g, '')
    .replace(/\s*\[system:match_data\][\s\S]*?(\[\/system\]|$)/g, '')
    .replace(/\s*\[system:job_context\][\s\S]*?(\[\/system\]|$)/g, '')
    .replace(/\s*\[system:revision_data\][\s\S]*?(\[\/system\]|$)/g, '')
    .trim()

// Strip AI intent markers from displayed content
export const stripIntentMarkers = (content: string) =>
  content
    .replace(/\n?\[GENERATE_RESUME\]\s*/g, '')
    .replace(/\n?\[RESUME_PREVIEW\]\s*/g, '')
    .replace(/\n?\[SUPPORT_REQUEST\]\s*/g, '')
    .replace(/\n?\[FOCUS:[^\]]+\]\s*/g, '')
    .replace(/\n?\[RESUME_CONTENT_UPDATED\]\s*/g, '')
    .replace(/\n?\[CONTENT:\{[\s\S]*\}\]\s*/g, '')
    .replace(/\n?\[LEARN_MODULE\]\s*/g, '')
    .replace(/\n?\[MODULE:\{[^}]*\}\]\s*/g, '')
    .replace(/\n?\[LEARN_PROGRESS:\{[^}]*\}\]\s*/g, '')
    .trim()

// Parse match data embedded in message content
export const parseMatchData = (content: string): MessageMatchData | undefined => {
  const match =
    content.match(/\[system:match_data\]([\s\S]*?)\[\/system\]/) ||
    content.match(/<!--MATCH_DATA:([\s\S]*?)-->/)
  if (match?.[1]) {
    try {
      return JSON.parse(match[1]) as MessageMatchData
    } catch {
      return undefined
    }
  }
  return undefined
}

// Parse job context embedded in message content
export const parseJobContext = (content: string): MessageJobContext | undefined => {
  const match = content.match(/\[system:job_context\]([\s\S]*?)\[\/system\]/)
  if (match?.[1]) {
    try {
      return JSON.parse(match[1]) as MessageJobContext
    } catch {
      return undefined
    }
  }
  return undefined
}

// Parse revision data embedded in message content
export const parseRevisionData = (content: string): RevisionReadyEvent | undefined => {
  const match = content.match(/\[system:revision_data\]([\s\S]*?)\[\/system\]/)
  if (match?.[1]) {
    try {
      return JSON.parse(match[1]) as RevisionReadyEvent
    } catch {
      return undefined
    }
  }
  return undefined
}

// Determine how to render a message based on its metadata and state
export type MessageDisplayType = 'streaming' | 'resume_upload' | 'error' | 'assistant' | 'user'

export function getMessageDisplayType(msg: ChatMessage): MessageDisplayType {
  if (msg.isStreaming && !msg.content) return 'streaming'
  if (msg.error && msg.role === 'assistant') return 'error'
  if (msg.msg_metadata?.type === 'resume_upload') return 'resume_upload'
  return msg.role
}

// URL detection helpers
export const isUrl = (text: string) => /^https?:\/\//i.test(text.trim())

// Domains that always require authentication
const PROTECTED_DOMAINS = ['linkedin.com']

export const isProtectedUrl = (url: string) =>
  PROTECTED_DOMAINS.some((domain) => url.toLowerCase().includes(domain))
